import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, type Location } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_CACHED_KEYS } from '@/lib/publicQueryCache';

const Admin = lazy(() => import('@/pages/Admin'));

// True while the persistent admin dashboard is the active screen.
export const AdminVisibleContext = createContext(true);
export const useAdminVisible = () => useContext(AdminVisibleContext);

// Sensitive admin caches removed when authorization is lost; public caches stay.
const SENSITIVE_PREFIXES = ['admin', 'ownership', 'playbook', 'user-roles', 'submission-quota'];
function isSensitiveKey(key: readonly unknown[]) {
  const head = String(key[0] ?? '');
  return (ADMIN_CACHED_KEYS as readonly string[]).includes(head) || SENSITIVE_PREFIXES.some(p => head.startsWith(p));
}

const isAdminPath = (pathname: string) => pathname === '/admin';

/** Route element for /admin: guards access; the dashboard itself lives in PersistentAdminShell. */
export function AdminRouteGate() {
  const { user, isAdmin, isLoading } = useAuth();
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }
  if (!user || !isAdmin) return <Navigate to="/" replace />;
  return null;
}

/**
 * Mounted once beside <Routes>. Lazily mounts the admin dashboard after an
 * authorized admin first opens /admin, keeps it mounted (hidden + inert) on
 * public routes, and unmounts it as soon as admin authorization is uncertain.
 */
export function PersistentAdminShell() {
  const location = useLocation();
  const { user, isAdmin, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const onAdmin = isAdminPath(location.pathname);
  const authorized = !isLoading && !!user && isAdmin;
  const [mounted, setMounted] = useState(false);
  const lastAdminLocation = useRef<Location | null>(null);
  if (onAdmin) lastAdminLocation.current = location;

  useEffect(() => {
    if (authorized && onAdmin) setMounted(true);
  }, [authorized, onAdmin]);

  // Sign-out, session expiry or privilege loss: unmount and drop admin data.
  useEffect(() => {
    if (authorized || !mounted) return;
    setMounted(false);
    lastAdminLocation.current = null;
    queryClient.cancelQueries({ predicate: q => isSensitiveKey(q.queryKey) });
    queryClient.removeQueries({ predicate: q => isSensitiveKey(q.queryKey) });
  }, [authorized, mounted, queryClient]);

  const visible = mounted && authorized && onAdmin;

  // When hidden: close any open admin dialogs/menus (portaled outside the
  // shell) and swallow the admin sidebar keyboard shortcut.
  useEffect(() => {
    if (!mounted || visible) return;
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    const block = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey)) e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', block, true);
    return () => window.removeEventListener('keydown', block, true);
  }, [mounted, visible]);

  if (!mounted || !authorized || !lastAdminLocation.current) return null;

  return (
    <AdminVisibleContext.Provider value={visible}>
      <div hidden={!visible} ref={el => { if (el) el.toggleAttribute('inert', !visible); }} aria-hidden={!visible || undefined} data-admin-shell>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
          {/* Frozen location: hidden admin keeps its own ?tab= instead of reading the public URL. */}
          <Routes location={lastAdminLocation.current}>
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Suspense>
      </div>
    </AdminVisibleContext.Provider>
  );
}
